import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ReportLayout, KPICard, exportToExcel } from '@/components/ReportLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { axiosInstance } from '../../App';
import { toast } from 'sonner';
import { 
  CreditCard, Wallet, Building2, Smartphone, Receipt, 
  ArrowUpCircle, ArrowDownCircle, AlertTriangle, TrendingUp
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell
} from 'recharts';

// Ícones por forma de pagamento
const FORMA_PAGAMENTO_ICONS = {
  pix: Smartphone,
  dinheiro: Wallet,
  cartao_debito: CreditCard,
  cartao_credito: CreditCard,
  boleto: Receipt,
  transferencia: Building2,
  cheque: Receipt,
  nao_informado: AlertTriangle,
};

// Cores por forma de pagamento
const FORMA_PAGAMENTO_COLORS = {
  pix: '#00D4AA',
  dinheiro: '#22C55E',
  cartao_debito: '#3B82F6',
  cartao_credito: '#8B5CF6',
  boleto: '#F59E0B',
  transferencia: '#6366F1',
  cheque: '#64748B',
  nao_informado: '#EF4444',
};

const RelFormaPagamento = ({ user, onLogout }) => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [periodoInicio, setPeriodoInicio] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [periodoFim, setPeriodoFim] = useState(() => {
    const now = new Date();
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${lastDay}`;
  });
  const company = JSON.parse(localStorage.getItem('company') || '{}');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(`/dfc/por-forma-pagamento/${company.id}`, { 
        params: { 
          periodo_inicio: periodoInicio,
          periodo_fim: periodoFim
        } 
      });
      setData(response.data);
    } catch (err) {
      setError('Erro ao carregar dados.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    if (company.id) fetchData(); 
  }, [company.id]);

  const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

  const handleExportExcel = () => {
    if (!data?.por_forma_pagamento) return;
    exportToExcel(data.por_forma_pagamento, [
      { key: 'label', label: 'Forma de Pagamento' },
      { key: 'entradas', label: 'Entradas' },
      { key: 'saidas', label: 'Saídas' },
      { key: 'liquido', label: 'Líquido' },
      { key: 'qtd_entradas', label: 'Qtd. Entradas' },
      { key: 'qtd_saidas', label: 'Qtd. Saídas' },
      { key: 'percentual_entradas', label: '% Entradas' },
      { key: 'percentual_saidas', label: '% Saídas' },
    ], 'movimentacao_forma_pagamento');
    toast.success('Exportado com sucesso!');
  };

  // Preparar dados para gráficos
  const chartData = data?.por_forma_pagamento?.map(item => ({
    name: item.label,
    entradas: item.entradas,
    saidas: item.saidas,
    liquido: item.liquido,
    fill: FORMA_PAGAMENTO_COLORS[item.forma_pagamento] || '#6366F1'
  })) || [];

  const pieDataEntradas = data?.por_forma_pagamento?.filter(item => item.entradas > 0).map(item => ({
    name: item.label,
    value: item.entradas,
    fill: FORMA_PAGAMENTO_COLORS[item.forma_pagamento] || '#6366F1'
  })) || [];

  const pieDataSaidas = data?.por_forma_pagamento?.filter(item => item.saidas > 0).map(item => ({
    name: item.label,
    value: item.saidas,
    fill: FORMA_PAGAMENTO_COLORS[item.forma_pagamento] || '#6366F1'
  })) || [];

  return (
    <ReportLayout
      user={user}
      onLogout={onLogout}
      title="Movimentação por Forma de Pagamento"
      subtitle="Análise de entradas e saídas por meio de pagamento"
      icon={CreditCard}
      iconColor="text-purple-400"
      iconBg="bg-purple-500/10"
      loading={loading}
      error={error}
      onRetry={fetchData}
      onExportExcel={handleExportExcel}
      reportId="forma-pagamento"
      filters={
        <div className="flex gap-4 items-center flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-400">De:</span>
            <Input 
              type="date" 
              value={periodoInicio} 
              onChange={(e) => setPeriodoInicio(e.target.value)}
              className="w-[150px] bg-zinc-800 border-zinc-700"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-400">Até:</span>
            <Input 
              type="date" 
              value={periodoFim} 
              onChange={(e) => setPeriodoFim(e.target.value)}
              className="w-[150px] bg-zinc-800 border-zinc-700"
            />
          </div>
          <Button onClick={fetchData} className="bg-purple-600 hover:bg-purple-700">
            Filtrar
          </Button>
        </div>
      }
    >
      {data && (
        <>
          {/* Alertas */}
          {data.alertas?.length > 0 && (
            <div className="space-y-2 mb-6">
              {data.alertas.map((alerta, i) => (
                <div 
                  key={i} 
                  className={`p-4 rounded-lg flex items-start gap-3 ${
                    alerta.tipo === 'warning' ? 'bg-yellow-500/10 border border-yellow-500/30' : 
                    alerta.tipo === 'danger' ? 'bg-red-500/10 border border-red-500/30' : 
                    'bg-blue-500/10 border border-blue-500/30'
                  }`}
                >
                  <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${
                    alerta.tipo === 'warning' ? 'text-yellow-400' : 
                    alerta.tipo === 'danger' ? 'text-red-400' : 'text-blue-400'
                  }`} />
                  <span className="text-sm text-gray-300">{alerta.mensagem}</span>
                </div>
              ))}
            </div>
          )}

          {/* KPIs Resumo */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <KPICard 
              title="Total Entradas" 
              value={formatCurrency(data.resumo?.total_entradas)} 
              icon={ArrowUpCircle} 
              color="text-green-400" 
            />
            <KPICard 
              title="Total Saídas" 
              value={formatCurrency(data.resumo?.total_saidas)} 
              icon={ArrowDownCircle} 
              color="text-red-400" 
            />
            <KPICard 
              title="Resultado Líquido" 
              value={formatCurrency(data.resumo?.liquido)} 
              icon={TrendingUp} 
              color={data.resumo?.liquido >= 0 ? 'text-green-400' : 'text-red-400'} 
            />
          </div>

          {/* Gráfico de Barras - Entradas vs Saídas */}
          <Card className="bg-zinc-900 border-zinc-800 mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-purple-400" />
                Comparativo por Forma de Pagamento
              </CardTitle>
            </CardHeader>
            <CardContent>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={chartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis type="number" stroke="#71717a" tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
                    <YAxis type="category" dataKey="name" stroke="#71717a" width={120} fontSize={12} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a' }} 
                      formatter={(v) => formatCurrency(v)} 
                    />
                    <Legend />
                    <Bar dataKey="entradas" name="Entradas" fill="#22c55e" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="saidas" name="Saídas" fill="#ef4444" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-zinc-500">
                  Sem movimentações no período
                </div>
              )}
            </CardContent>
          </Card>

          {/* Gráficos de Pizza lado a lado */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Pizza Entradas */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-400">
                  <ArrowUpCircle className="w-5 h-5" />
                  Distribuição de Entradas
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pieDataEntradas.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={pieDataEntradas}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {pieDataEntradas.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => formatCurrency(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-64 flex items-center justify-center text-zinc-500">
                    Sem entradas no período
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pizza Saídas */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-400">
                  <ArrowDownCircle className="w-5 h-5" />
                  Distribuição de Saídas
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pieDataSaidas.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={pieDataSaidas}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {pieDataSaidas.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => formatCurrency(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-64 flex items-center justify-center text-zinc-500">
                    Sem saídas no período
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Tabela Detalhada */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle>Detalhamento por Forma de Pagamento</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="text-left py-3 px-4 text-zinc-400">Forma de Pagamento</th>
                      <th className="text-right py-3 px-4 text-green-400">Entradas</th>
                      <th className="text-right py-3 px-4 text-zinc-400">Qtd.</th>
                      <th className="text-right py-3 px-4 text-red-400">Saídas</th>
                      <th className="text-right py-3 px-4 text-zinc-400">Qtd.</th>
                      <th className="text-right py-3 px-4 text-zinc-400">Líquido</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.por_forma_pagamento?.map((item, i) => {
                      const Icon = FORMA_PAGAMENTO_ICONS[item.forma_pagamento] || CreditCard;
                      return (
                        <tr key={i} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <div 
                                className="p-2 rounded-lg" 
                                style={{ backgroundColor: `${FORMA_PAGAMENTO_COLORS[item.forma_pagamento]}20` }}
                              >
                                <Icon 
                                  className="w-4 h-4" 
                                  style={{ color: FORMA_PAGAMENTO_COLORS[item.forma_pagamento] }} 
                                />
                              </div>
                              <span className="text-white font-medium">{item.label}</span>
                              {item.forma_pagamento === 'nao_informado' && (
                                <Badge className="bg-red-500/20 text-red-400 text-xs">Pendente</Badge>
                              )}
                            </div>
                          </td>
                          <td className="text-right py-3 px-4 text-green-400 font-semibold">
                            {formatCurrency(item.entradas)}
                            {item.percentual_entradas > 0 && (
                              <span className="text-xs text-zinc-500 ml-1">({item.percentual_entradas}%)</span>
                            )}
                          </td>
                          <td className="text-right py-3 px-4 text-zinc-400">{item.qtd_entradas}</td>
                          <td className="text-right py-3 px-4 text-red-400 font-semibold">
                            {formatCurrency(item.saidas)}
                            {item.percentual_saidas > 0 && (
                              <span className="text-xs text-zinc-500 ml-1">({item.percentual_saidas}%)</span>
                            )}
                          </td>
                          <td className="text-right py-3 px-4 text-zinc-400">{item.qtd_saidas}</td>
                          <td className={`text-right py-3 px-4 font-bold ${item.liquido >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {formatCurrency(item.liquido)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-zinc-800/50 font-bold">
                      <td className="py-3 px-4 text-white">TOTAL</td>
                      <td className="text-right py-3 px-4 text-green-400">{formatCurrency(data.resumo?.total_entradas)}</td>
                      <td className="text-right py-3 px-4 text-zinc-400">
                        {data.por_forma_pagamento?.reduce((acc, item) => acc + item.qtd_entradas, 0)}
                      </td>
                      <td className="text-right py-3 px-4 text-red-400">{formatCurrency(data.resumo?.total_saidas)}</td>
                      <td className="text-right py-3 px-4 text-zinc-400">
                        {data.por_forma_pagamento?.reduce((acc, item) => acc + item.qtd_saidas, 0)}
                      </td>
                      <td className={`text-right py-3 px-4 ${data.resumo?.liquido >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatCurrency(data.resumo?.liquido)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </ReportLayout>
  );
};

export default RelFormaPagamento;
